/**
 * Date Preferences Hook
 *
 * Provides user's timezone and locale preferences throughout the application.
 * Uses the auth context to get user preferences and provides formatted date functions.
 */

import { createContext, useContext, useMemo, useCallback, ReactNode } from 'react'
import { useAuth } from './useAuth'
import {
  formatDate as formatDateUtil,
  formatDateTime as formatDateTimeUtil,
  formatTime as formatTimeUtil,
  formatRelativeTime as formatRelativeTimeUtil,
  formatChartDate as formatChartDateUtil,
  formatLongDate as formatLongDateUtil,
  formatDateRange as formatDateRangeUtil,
  UserDatePreferences,
  getBrowserTimezone,
  getBrowserLocale,
} from '../lib/dateUtils'

interface DatePreferencesContextValue {
  /** User's timezone (IANA format) */
  timezone: string
  /** User's locale (BCP 47 format) */
  locale: string
  /** Format a timestamp as a date string */
  formatDate: (timestamp: number | Date | null | undefined, options?: Intl.DateTimeFormatOptions) => string
  /** Format a timestamp as a date and time string */
  formatDateTime: (timestamp: number | Date | null | undefined, options?: Intl.DateTimeFormatOptions) => string
  /** Format a timestamp as a time string */
  formatTime: (timestamp: number | Date | null | undefined, options?: Intl.DateTimeFormatOptions) => string
  /** Format a timestamp as a relative time string */
  formatRelativeTime: (timestamp: number | Date | null | undefined) => string
  /** Format a timestamp for charts (short format) */
  formatChartDate: (timestamp: number | Date | null | undefined) => string
  /** Format a timestamp in long format */
  formatLongDate: (timestamp: number | Date | null | undefined) => string
  /** Format a date range */
  formatDateRange: (start: number | Date | null | undefined, end: number | Date | null | undefined) => string
  /** User preferences object for direct use */
  preferences: UserDatePreferences
}

const DatePreferencesContext = createContext<DatePreferencesContextValue | null>(null)

interface DatePreferencesProviderProps {
  children: ReactNode
}

export function DatePreferencesProvider({ children }: DatePreferencesProviderProps) {
  const { user } = useAuth()

  // Extract preferences from user object
  const preferences = useMemo<UserDatePreferences>(() => {
    let timezone = 'UTC'
    let locale = 'en-US'

    if (user?.preferences) {
      // User preferences could be a string (JSON) or already parsed object
      let prefs = user.preferences
      if (typeof prefs === 'string') {
        try {
          prefs = JSON.parse(prefs)
        } catch {
          prefs = {}
        }
      }

      if (prefs.timezone) {
        timezone = prefs.timezone
      }
      if (prefs.locale) {
        locale = prefs.locale
      }
    }

    return { timezone, locale }
  }, [user?.preferences])

  const formatDate = useCallback(
    (timestamp: number | Date | null | undefined, options?: Intl.DateTimeFormatOptions) =>
      formatDateUtil(timestamp, preferences, options),
    [preferences]
  )

  const formatDateTime = useCallback(
    (timestamp: number | Date | null | undefined, options?: Intl.DateTimeFormatOptions) =>
      formatDateTimeUtil(timestamp, preferences, options),
    [preferences]
  )

  const formatTime = useCallback(
    (timestamp: number | Date | null | undefined, options?: Intl.DateTimeFormatOptions) =>
      formatTimeUtil(timestamp, preferences, options),
    [preferences]
  )

  const formatRelativeTime = useCallback(
    (timestamp: number | Date | null | undefined) => formatRelativeTimeUtil(timestamp, preferences),
    [preferences]
  )

  const formatChartDate = useCallback(
    (timestamp: number | Date | null | undefined) => formatChartDateUtil(timestamp, preferences),
    [preferences]
  )

  const formatLongDate = useCallback(
    (timestamp: number | Date | null | undefined) => formatLongDateUtil(timestamp, preferences),
    [preferences]
  )

  const formatDateRange = useCallback(
    (start: number | Date | null | undefined, end: number | Date | null | undefined) =>
      formatDateRangeUtil(start, end, preferences),
    [preferences]
  )

  const value = useMemo<DatePreferencesContextValue>(
    () => ({
      timezone: preferences.timezone || 'UTC',
      locale: preferences.locale || 'en-US',
      formatDate,
      formatDateTime,
      formatTime,
      formatRelativeTime,
      formatChartDate,
      formatLongDate,
      formatDateRange,
      preferences,
    }),
    [preferences, formatDate, formatDateTime, formatTime, formatRelativeTime, formatChartDate, formatLongDate, formatDateRange]
  )

  return <DatePreferencesContext.Provider value={value}>{children}</DatePreferencesContext.Provider>
}

/**
 * Hook to access date formatting functions that respect user preferences
 */
export function useDatePreferences(): DatePreferencesContextValue {
  const context = useContext(DatePreferencesContext)

  if (!context) {
    // Return default functions if provider is not available (for backwards compatibility)
    const defaultPreferences: UserDatePreferences = {
      timezone: getBrowserTimezone(),
      locale: getBrowserLocale(),
    }

    return {
      timezone: defaultPreferences.timezone || 'UTC',
      locale: defaultPreferences.locale || 'en-US',
      formatDate: (ts, opts) => formatDateUtil(ts, defaultPreferences, opts),
      formatDateTime: (ts, opts) => formatDateTimeUtil(ts, defaultPreferences, opts),
      formatTime: (ts, opts) => formatTimeUtil(ts, defaultPreferences, opts),
      formatRelativeTime: (ts) => formatRelativeTimeUtil(ts, defaultPreferences),
      formatChartDate: (ts) => formatChartDateUtil(ts, defaultPreferences),
      formatLongDate: (ts) => formatLongDateUtil(ts, defaultPreferences),
      formatDateRange: (start, end) => formatDateRangeUtil(start, end, defaultPreferences),
      preferences: defaultPreferences,
    }
  }

  return context
}
